/**
 * MonitorReviewStep - Wizard step 5 for final review and creation
 *
 * Displays:
 * - Complete configuration summary
 * - Edit buttons to return to specific steps
 * - Create Monitor button with loading state
 * - Validation summary
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle,
  Edit,
  FolderOpen,
  FileText,
  Clock,
  Database,
  AlertCircle,
  Layers,
  ArrowRight,
} from 'lucide-react';
import {
  useMonitorCreationStore,
  MonitorCreationStep,
} from '@/features/monitors/store/monitorCreationStore';
import { useCreateMonitor } from '@/features/monitors/hooks/useMonitors';
import { useToast } from '@/hooks/useToast';
import { MonitorTypeBadge } from './MonitorTypeBadge';

/**
 * MonitorReviewStep Component
 *
 * Final step of monitor creation wizard for review and creation.
 *
 * @example
 * ```tsx
 * <MonitorReviewStep />
 * ```
 */
export const MonitorReviewStep: React.FC = () => {
  const navigate = useNavigate();
  const store = useMonitorCreationStore();
  const createMonitor = useCreateMonitor();
  const { toast } = useToast();

  /**
   * Handle create monitor
   */
  const handleCreate = async () => {
    try {
      // Build config with level partition mappings
      const config = {
        ...store.config,
        ...(store.level_partition_mappings.length > 0 && {
          level_partition_mappings: store.level_partition_mappings,
        }),
      };

      const response = await createMonitor.mutateAsync({
        name: store.name,
        description: store.description,
        dataset_id: store.dataset_id,
        directory_path: store.directory_path,
        file_patterns: store.file_patterns,
        scan_frequency: store.scan_frequency,
        monitor_type: store.monitor_type,
        auto_ingest: store.auto_ingest,
        trigger_delay_seconds: store.trigger_delay_seconds,
        batch_size: store.batch_size,
        status: store.status,
        default_schema_id: store.default_schema_id,
        config: Object.keys(config).length > 0 ? config : undefined,
      });

      toast({
        title: 'Monitor Created',
        description: `Monitor "${response.monitor.name}" has been created successfully`,
      });

      store.resetWizard();
      navigate(`/monitors/${response.monitor.id}`);
    } catch (error: any) {
      console.error('Failed to create monitor:', error);

      toast({
        title: 'Creation Failed',
        description:
          error.response?.data?.detail ||
          'Failed to create monitor. Please check your configuration and try again.',
        variant: 'destructive',
      });
    }
  };

  /**
   * Validate all required fields
   */
  const isValid = !!(
    store.name &&
    store.dataset_id &&
    store.directory_path &&
    store.directoryValidated &&
    store.file_patterns.include_patterns.length > 0 &&
    store.scan_frequency &&
    store.monitor_type
  );

  return (
    <div className="space-y-6">
      {/* Validation Status */}
      {isValid ? (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Configuration Valid</strong>
            <p className="mt-1">All required fields have been completed. Ready to create monitor.</p>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Configuration Incomplete</strong>
            <p className="mt-1">Please complete all required fields before creating the monitor.</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Basic Info Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Basic Information
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => store.goToStep(MonitorCreationStep.BASIC_INFO)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-muted-foreground">Monitor Name</p>
            <p className="font-semibold">{store.name || '—'}</p>
          </div>
          {store.description && (
            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="text-sm">{store.description}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground">Target Dataset ID</p>
            <p className="font-mono text-sm">{store.dataset_id || '—'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Directory Config Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Directory Configuration
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => store.goToStep(MonitorCreationStep.DIRECTORY_CONFIG)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-muted-foreground">Directory Path</p>
            <p className="font-mono text-sm font-semibold">{store.directory_path || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Validation Status</p>
            {store.directoryValidated ? (
              <Badge variant="default" className="bg-success/10 text-success border-success">
                <CheckCircle className="h-3 w-3 mr-1" />
                Validated
              </Badge>
            ) : (
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                Not Validated
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Partition Mapping Review */}
      {store.level_partition_mappings.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Partition Mappings
                </CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => store.goToStep(MonitorCreationStep.PARTITION_MAPPING)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {store.level_partition_mappings
                .filter((m) => m.partition_name)
                .map((mapping) => (
                  <div
                    key={mapping.level}
                    className="flex items-center gap-3 p-2 rounded bg-muted/50"
                  >
                    <Badge variant="outline" className="font-mono">
                      Level {mapping.level}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="default">{mapping.partition_name}</Badge>
                    {mapping.sample_values && mapping.sample_values.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        (e.g., {mapping.sample_values.slice(0, 2).join(', ')}
                        {mapping.sample_values.length > 2 && '...'})
                      </span>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Patterns Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                File Patterns
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => store.goToStep(MonitorCreationStep.FILE_PATTERNS)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-muted-foreground mb-2">Include Patterns</p>
            {store.file_patterns.include_patterns.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {store.file_patterns.include_patterns.map((pattern, index) => (
                  <Badge key={index} variant="default">
                    <span className="font-mono">{pattern}</span>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
          </div>

          {store.file_patterns.exclude_patterns.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Exclude Patterns</p>
              <div className="flex flex-wrap gap-2">
                {store.file_patterns.exclude_patterns.map((pattern, index) => (
                  <Badge key={index} variant="outline">
                    <span className="font-mono">{pattern}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">Recursive Scanning</p>
            <Badge variant={store.file_patterns.recursive ? 'default' : 'secondary'}>
              {store.file_patterns.recursive ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Review */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Schedule Configuration
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => store.goToStep(MonitorCreationStep.SCHEDULE)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Scan Frequency</p>
              <p className="font-semibold">{store.scan_frequency || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monitor Type</p>
              {store.monitor_type ? (
                <MonitorTypeBadge type={store.monitor_type} />
              ) : (
                <p>—</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Auto-Ingest</p>
              <Badge variant={store.auto_ingest ? 'default' : 'secondary'}>
                {store.auto_ingest ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Trigger Delay</p>
              <p className="font-semibold">{store.trigger_delay_seconds}s</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Batch Size</p>
              <p className="font-semibold">{store.batch_size} files</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Button */}
      <div className="flex justify-end pt-6 border-t">
        <Button
          size="lg"
          onClick={handleCreate}
          disabled={!isValid || createMonitor.isPending}
          className="min-w-[200px]"
        >
          {createMonitor.isPending ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Creating Monitor...
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5 mr-2" />
              Create Monitor
            </>
          )}
        </Button>
      </div>

      {/* Creation Hint */}
      {!createMonitor.isPending && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Once created, the monitor will become active and start scanning according to the
            configured schedule. You can pause or modify the monitor at any time.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
